import cn from 'classnames';
import Button from 'components/button-new';
import PaymentMethodIcons from 'components/paymen-method-icons';
import { useConfig } from 'config/use-config';
import { ShopId } from 'constants/shop';
import useNavigationData from 'hooks/navigation/use-navigation-data';
import Link from 'next/link';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Gtm } from 'tracking';
import { formatPhoneNumber } from 'utils/phone';
import { isAbsoluteUrl } from 'utils/url/url';
import IconCircleMinus from '../../../assets/icons/circle-minus.svg';
import IconCirclePlus from '../../../assets/icons/circle-plus.svg';
import IconEmail from '../../../assets/icons/email.svg';
import IconPhone from '../../../assets/icons/phone.svg';
import IconWhatsapp from '../../../assets/icons/whatsapp.svg';
import SmallCaps from '../../../components/typography/small-caps';
import LocaleSwitcher from '../locale-switcher';
import DeliveryLogos from './delivery-logos';
import TrustedShops from './trusted-shops';

type ActiveSection = string;

const LinkSection: React.FC<{ isCheckout: boolean }> = ({ isCheckout }) => {
  const { featureFlags, shopId, customerHotline, helpUrl, whatsappNumber, withdrawalFormUrl } =
    useConfig();
  const [activeSection, setSection] = React.useState<ActiveSection | null>(null);
  const { footer, companyInfo } = useNavigationData();
  const featureFlg = featureFlags.localeSwitcher;
  const { formatMessage } = useIntl();
  const showWithdrawalButton = shopId === ShopId.DE;
  const withdrawalAriaLabel = formatMessage({ id: 'footer:link-section:widerruf-aria' });

  const footerLink = ({
    title,
    url,
    track,
  }: {
    title: string;
    url: string;
    track?: string;
  }): React.ReactNode => {
    if (isAbsoluteUrl(url)) {
      return (
        <a
          href={url}
          key={url}
          className="text-grey-700 lg:hover:text-grey-900 block text-sm lg:text-base"
          target="_blank"
          rel="noreferrer"
        >
          {title}
        </a>
      );
    }
    return (
      <Link
        data-test={`link-section-item-${title}`}
        href={url}
        passHref
        key={url}
        className="text-grey-700 lg:hover:text-grey-900 block text-sm lg:text-base"
        onClick={() => {
          if (track) {
            Gtm.track({
              group: Gtm.Group.Component,
              name: Gtm.ComponentEvent.Click,
              data: { label: track },
            });
          }
        }}
      >
        {title}
      </Link>
    );
  };

  const linkToggle = (translationId: string, navigationId: ActiveSection): React.ReactNode => {
    return (
      <button
        onClick={() => {
          setSection(navigationId === activeSection ? null : navigationId);
        }}
        className="bg-grey-100 mt-2 flex w-full justify-between px-6 pt-3 pb-2 text-left focus:outline-hidden md:pointer-events-none md:mt-0 md:w-auto md:bg-transparent md:px-0 md:pt-0 md:pb-0 lg:cursor-default"
      >
        <SmallCaps>
          <div className="text-primary-main">
            <FormattedMessage id={translationId} />
          </div>
        </SmallCaps>

        {navigationId === activeSection ? (
          <IconCircleMinus className="text-grey-400 h-3 w-3 md:hidden" />
        ) : (
          <IconCirclePlus className="text-grey-400 h-3 w-3 md:hidden" />
        )}
      </button>
    );
  };

  return (
    <>
      {!isCheckout && (
        <div className="container w-screen overflow-hidden md:mb-8 md:pt-10">
          <div className="-mx-4 flex flex-col md:flex-row lg:-mx-8">
            {footer.items.map((item, index) => (
              <div
                className={cn(
                  'border-grey-200 order-last flex-1 border-l md:order-first md:px-4 lg:px-8',
                  index === 0 && 'border-l-0'
                )}
                key={item.title}
              >
                {linkToggle(item.title, item.title)}

                <div
                  className={cn(
                    activeSection !== item.title && 'hidden md:block',
                    'p-6 md:p-0 md:pt-2 lg:-mx-4 lg:flex'
                  )}
                  data-test={`link-section-item-${item.title}`}
                >
                  <div className="lg:px-4">{item.items.map((link) => footerLink(link))}</div>
                </div>
              </div>
            ))}
            <div className="border-grey-200 order-first border-l px-8 pt-6 pb-4 md:order-last md:max-w-sm md:px-4 md:pb-0 lg:max-w-md lg:px-8 xl:max-w-sm xl:pt-0">
              <div className="text-primary-main text-center leading-tight font-black md:text-left">
                <FormattedMessage id="footer:help-title" />
              </div>

              <div className="text-grey-700 flex flex-col items-center pt-2 md:block">
                <div className="flex">
                  <IconPhone className="mt-1 mr-4 h-4 w-4" />
                  <div>
                    <a href={`tel:+${customerHotline}`}>
                      <FormattedMessage
                        id="footer:link-section:customer-support-phone-number"
                        values={{ phone: formatPhoneNumber(customerHotline) }}
                      />
                    </a>
                    <br />
                    <small className="text-text-light-bg-secondary -mt-1 block text-xs">
                      <FormattedMessage id="footer:link-section:opening-hours" />
                    </small>
                  </div>
                </div>
                <div className="mb-3 flex items-center">
                  <IconEmail className="mr-4 h-4 w-4" />
                  <div className="flex flex-col">
                    <a href={helpUrl} target="_blank" rel="noreferrer">
                      <FormattedMessage id="footer:link-section:link-help" />
                    </a>
                  </div>
                </div>
                <div className="flex">
                  <IconWhatsapp className="mt-1 mr-4 h-4 w-4" />
                  <div>
                    <a
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      href={`https://wa.me/${whatsappNumber}`}
                    >
                      <FormattedMessage
                        id="footer:link-section:whatsapp-number"
                        values={{ whatsapp: formatPhoneNumber(whatsappNumber) }}
                      />
                    </a>
                    <br />
                    <small className="text-text-light-bg-secondary -mt-1 block text-xs">
                      <FormattedMessage id="footer:link-section:opening-hours" />
                    </small>
                  </div>
                </div>
              </div>

              {showWithdrawalButton && (
                <div className="hidden pt-4 lg:block">
                  <Button
                    href={withdrawalFormUrl}
                    target="_blank"
                    rel="noreferrer"
                    layout="outline"
                    color="red"
                    size="base"
                    aria-label={withdrawalAriaLabel}
                    data-test="footer-widerruf-button-desktop"
                  >
                    <FormattedMessage id="footer:link-section:widerruf-button" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="md:bg-grey-100 pb-32 lg:py-6">
        <div className="container">
          <div className="-mx-4 flex flex-col md:flex-row lg:-mx-8">
            {featureFlg && <LocaleSwitcher className="mx-8 mt-4 lg:mt-0" />}

            {showWithdrawalButton && (
              <div className="my-4 px-4 lg:hidden">
                <Button
                  href={withdrawalFormUrl}
                  target="_blank"
                  rel="noreferrer"
                  layout="outline"
                  color="red"
                  size="base"
                  aria-label={withdrawalAriaLabel}
                  data-test="footer-widerruf-button-mobile"
                >
                  <FormattedMessage id="footer:link-section:widerruf-button" />
                </Button>
              </div>
            )}
            <DeliveryLogos />

            <div className="hidden flex-col justify-center px-4 pt-6 text-center md:justify-start md:pt-0 md:text-left lg:block lg:px-8">
              <SmallCaps>
                <div className="text-primary-main">
                  <FormattedMessage id="footer:title-payment" />
                </div>
              </SmallCaps>

              <PaymentMethodIcons className="mt-2" />
            </div>

            {!isCheckout && <TrustedShops />}

            {!isCheckout && (
              <div className="order-first md:order-last md:ml-auto md:px-4 lg:px-8">
                {companyInfo.items.map((item) => (
                  <React.Fragment key={item.title}>
                    {linkToggle(item.title, item.title)}

                    <div
                      className={cn(
                        activeSection !== item.title && 'hidden md:flex',
                        'flex flex-col flex-wrap p-6 md:flex-row md:p-0 md:pt-2'
                      )}
                    >
                      {item.items.map((item) => (
                        <div className="pr-2" key={item.title}>
                          {footerLink({ title: item.title, url: item.url })}
                        </div>
                      ))}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LinkSection;
